import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { OAuthService } from "./oauth.service";
import { map, switchMap, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { IJiraIssue } from "../jira-issue.interface";
import { IJiraResponse } from "../jira-response.interface";

interface IBody {
  comment: string;
  started: string;
  timeSpentSeconds: number;
}

interface IWorklogs {
  worklogs: IWorklog[];
}

interface IWorklog {
  id: string;
  timeSpentSeconds: number;
  comment: string;
}

@Injectable({
  providedIn: "root",
})
export class JiraService {
  httpClient = inject(HttpClient);
  private _oAuthService = inject(OAuthService);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public jiraDomain: string = environment.jiraDomain;
  public headers: HttpHeaders = new HttpHeaders({ "Content-type": "application/json" });
  public requestOptions = {
    headers: this.headers,
    withCredentials: true,
  };
  // type Object because Angular has no type
  public requestOptionsWithObserve: {} = {
    headers: this.headers,
    withCredentials: true,
    observe: "response",
  };
  public addIssueWorklog(issueKey: string, body: Object): Observable<Object> {
    return this.httpClient.post(
      `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}/worklog`,
      body,
      this.requestOptionsWithObserve,
    );
  }

  public deleteIssueWorklog(issueKey: string, worklogId: string): Observable<Object> {
    return this.getIssueWorklog(issueKey, worklogId).pipe(
      map((worklog: IWorklog) => worklog.timeSpentSeconds),
      switchMap((timeSpent) => {
        return this.checkRemainingEstimate(issueKey, timeSpent, worklogId, "delete").pipe(
          switchMap((adjustEstimate) => {
            return this.httpClient.delete(
              `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}/worklog/${worklogId}?adjustEstimate=${adjustEstimate}`,
              this.requestOptionsWithObserve,
            );
          }),
        );
      }),
    );
  }

  public getIssue(issueKey: string): Observable<IJiraIssue> {
    return this.httpClient.get<IJiraIssue>(
      `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}`,
      this.requestOptions,
    );
  }

  public getIssueWorklogs(issueKey: string): Observable<IWorklogs> {
    return this.httpClient.get<IWorklogs>(
      `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}/worklog`,
      this.requestOptions,
    );
  }

  public getJiraUser(): Observable<Object> {
    return this.httpClient.get(
      `${this._oAuthService.jiraUrl$.value}/myself`,
      { withCredentials: true },
    );
  }

  public updateIssueWorklog(issueKey: string, worklogId: string, body: IBody): Observable<IJiraResponse> {
    return this.checkRemainingEstimate(issueKey, body.timeSpentSeconds, worklogId, "update").pipe(
      switchMap((adjustEstimate) => {
        return this.httpClient.put<IJiraResponse>(
          `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}/worklog/${worklogId}?adjustEstimate=${adjustEstimate}`,
          body,
          this.requestOptionsWithObserve,
        );
      }),
    );
  }

  public getIssueWorklog(issueKey: string, worklogId: string): Observable<IWorklog> {
    return this.httpClient.get<IWorklog>(
      `${this._oAuthService.jiraUrl$.value}/issue/${issueKey}/worklog/${worklogId}`,
      this.requestOptions,
    );
  }

  public checkRemainingEstimate(issueKey: string, timeSpent: number, worklogId: string, operation: string): Observable<string> {
    let loggedTime: number = 0;
    let timeEstimate: number = 0;
    let oldTime: number = 0;

    return this.getIssue(issueKey).pipe(
      tap((issue: IJiraIssue) => timeEstimate = issue.fields.aggregatetimeoriginalestimate),
      switchMap(() => {
        return this.getIssueWorklogs(issueKey).pipe(
          tap((worklogsResult: IWorklogs) => worklogsResult.worklogs.forEach((worklog: IWorklog) => {
            loggedTime += worklog.timeSpentSeconds;

            if (worklog.id === worklogId) {
              oldTime = worklog.timeSpentSeconds;
            }
          })),
        );
      }),
      map(() => {
        if (timeSpent > oldTime || (oldTime === timeSpent && operation === "update")) {
          return "auto";
        } else if (operation === "delete" && (timeEstimate - loggedTime + timeSpent) <= 0) {
          return "leave";
        } else if (operation === "delete" && (timeEstimate - loggedTime + timeSpent) > 0) {
          return `new&newEstimate=${(timeEstimate - loggedTime + timeSpent) / 60}m`;
        } else if ((timeEstimate - loggedTime + oldTime - timeSpent) <= 0) {
          return "leave";
        } else if ((timeEstimate - loggedTime + oldTime - timeSpent) > 0) {
          return `new&newEstimate=${(timeEstimate - loggedTime + oldTime - timeSpent) / 60}m`;
        }
      }),
    );
  }

}
